/**
 * Formats fr-FR de la famille Tableau — des nombres qu'on lit d'un coup d'œil.
 *
 * Les milliers sont séparés par une ESPACE FINE INSÉCABLE (U+202F : « 7 327 »),
 * quelle que soit la version d'ICU du moteur (certaines rendent U+00A0) : on
 * normalise, pour qu'un test et un écran disent la même chose.
 *
 * Candidats à remonter dans `src/shared/` à l'intégration : la famille « stat »
 * porte les mêmes besoins.
 */

/** Espace fine insécable — celle des milliers en typographie française. */
export const ESPACE_FINE = " ";

const SYMBOLES: Record<string, string> = { EUR: "€", USD: "$US", GBP: "£", CHF: "CHF", USDT: "USDT" };

function normaliser(s: string): string {
  return s.replace(/[  ]/g, ESPACE_FINE);
}

/** `7327` → « 7 327 » ; `8.034` avec 2 décimales → « 8,03 ». */
export function formatNombre(n: number, decimales = 0): string {
  if (!Number.isFinite(n)) return "—";
  return normaliser(
    new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    }).format(n),
  );
}

/** `34.99, "EUR"` → « 34,99 € » ; `7327, "USD"` → « 7 327 $US » (un entier n'a pas de centimes). */
export function formatMontant(n: number, devise = "EUR", decimales?: number): string {
  if (!Number.isFinite(n)) return "—";
  const d = decimales ?? (Number.isInteger(n) ? 0 : 2);
  const symbole = SYMBOLES[devise];
  if (symbole) return `${formatNombre(n, d)}${ESPACE_FINE}${symbole}`;
  return normaliser(
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: devise,
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }).format(n),
  );
}

/** « 87,9 k$US », « 1,2 M€ » — pour une tuile, jamais pour une cellule de table. */
export function formatMontantCourt(n: number, devise = "EUR"): string {
  if (!Number.isFinite(n)) return "—";
  const symbole = SYMBOLES[devise] ?? devise;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${formatNombre(n / 1_000_000, 1)}${ESPACE_FINE}M${symbole}`;
  if (abs >= 10_000) return `${formatNombre(n / 1_000, 1)}${ESPACE_FINE}k${symbole}`;
  return formatMontant(n, devise);
}

/** « il y a 3 min », « il y a 2 h », « hier », « il y a 12 j » — depuis `maintenant` (injectable pour les tests). */
export function formatDepuis(date: Date, maintenant: Date = new Date()): string {
  const s = Math.round((maintenant.getTime() - date.getTime()) / 1000);
  if (s < 60) return "à l'instant";
  const rtf = new Intl.RelativeTimeFormat("fr", { numeric: "auto" });
  const min = Math.round(s / 60);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.round(h / 24);
  if (j < 30) return normaliser(rtf.format(-j, "day")).replace("jours", "j").replace("jour", "j");
  const mois = Math.round(j / 30);
  if (mois < 12) return normaliser(rtf.format(-mois, "month"));
  return normaliser(rtf.format(-Math.round(mois / 12), "year"));
}
